/**
 * KK-1402 · Öğrenci listesi içe aktarımı — KURULUMUN KENDİSİ.
 *
 * Bu ekranın tek işi vardır: YANLIŞ KOLON EŞLEMESİYLE 1.000 BOZUK KİŞİ
 * ÜRETİLMESİNİ ENGELLEMEK. Yükleme hiçbir kişi oluşturmaz; yalnızca her satır
 * için bir karar üretir. Çakışma ve hatalar EN ÜSTE gelir, uygulama ancak
 * bunlar gözden geçirildikten sonra açılır.
 *
 * Satır sonuç kodları (servisler/iceaktarim.ts · SatirSonucu):
 *   1 yeni · 2 güncelleme · 3 çakışma · 4 hata · 5 atlandı
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Buton,
  Kart,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Uyari,
} from "@kantin/ui";
import { IstatistikKarosu } from "@kantin/ui/grafik";
import { apiDosyaIndir, apiDosyaYukle, apiOku, apiYaz } from "./api.js";
import {
  IslemDurumu,
  OnayKutusu,
  Sayfa,
  SayfaBasligi,
  Sayfalayici,
  SorguDurumu,
  hataMetni,
  tarihSaatGoster,
} from "./ortak.js";

// ---------------------------------------------------------------------------

const SatirSonucu = {
  YENI: 1,
  GUNCELLEME: 2,
  CAKISMA: 3,
  HATA: 4,
  ATLANDI: 5,
} as const;

const AktarimDurumu = {
  YUKLENDI: 1,
  ONIZLEME_HAZIR: 2,
  UYGULANDI: 3,
  IPTAL: 4,
} as const;

interface AktarimOzeti {
  id: string;
  dosya_adi: string;
  durum: number;
  toplam_satir: number;
  yeni_sayisi: number;
  guncellenen_sayisi: number;
  cakisan_sayisi: number;
  hatali_sayisi: number;
}

interface OnizlemeSatiri {
  id: string;
  satir_no: number;
  alanlar: {
    ad: string;
    soyad: string;
    sinif: string | null;
    numara: string | null;
    tur: number;
    alerji_notu: string | null;
    veli_ad: string | null;
    veli_soyad: string | null;
    veli_telefon: string | null;
    veli_eposta: string | null;
  } | null;
  kaynak: Record<string, string>;
  sonuc: number;
  sonuc_adi: string;
  eslesen_kisi_id: string | null;
  eslesen_kisi: string | null;
  mesaj: string | null;
}

interface OnizlemeYaniti extends AktarimOzeti {
  olusma_zamani: string;
  uygulama_zamani: string | null;
  sayfa: number;
  boyut: number;
  satir_toplami: number;
  satirlar: OnizlemeSatiri[];
}

interface UygulamaSonucu {
  aktarim_id: string;
  olusturulan: number;
  guncellenen: number;
  atlanan: number;
  hatalar: { satir_no: number; mesaj: string }[];
}

const SAYFA_BOYU = 25;
/** Sunucunun kabul ettiği en büyük sayfa (zSayfa.boyut max). */
const TOPLU_BOYUT = 500;

// ---------------------------------------------------------------------------

export function IceAktarim() {
  const istemci = useQueryClient();
  const [aktarimId, setAktarimId] = useState<string | null>(null);
  const [dosya, setDosya] = useState<File | null>(null);
  const [indirmeHatasi, setIndirmeHatasi] = useState<unknown>(null);

  const yukle = useMutation({
    mutationFn: (d: File) => apiDosyaYukle<AktarimOzeti>("/ice-aktarim", d),
    onSuccess: (o) => setAktarimId(o.id),
  });

  const sablonIndir = useMutation({
    mutationFn: () =>
      apiDosyaIndir("/ice-aktarim/sablon", "kantin-ogrenci-sablonu.csv"),
    onError: (e) => setIndirmeHatasi(e),
    onSuccess: () => setIndirmeHatasi(null),
  });

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="İçe aktarım"
        aciklama="Öğrenci ve personel listesini .xlsx veya .csv dosyasından aktarın. Yükleme hiçbir kayıt oluşturmaz — önce önizleme üretilir, kararları siz verirsiniz."
      />

      <Kart baslik="1 · Dosyayı hazırlayın">
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-murekkep-2">
            Şablondaki kolon başlıklarını değiştirmeyin. Ayraç{" "}
            <strong>noktalı virgül</strong> (;) olmalıdır — Türkçe Excel yerel
            ayarı böyle kaydeder. Tür sütununa{" "}
            <code className="rakam font-mono text-xs">ogrenci</code> veya{" "}
            <code className="rakam font-mono text-xs">personel</code> yazın.
          </p>
          <div>
            <Buton
              tur="ikincil"
              onClick={() => sablonIndir.mutate()}
              yukleniyor={sablonIndir.isPending}
            >
              Örnek şablonu indir (.csv)
            </Buton>
          </div>
          {indirmeHatasi ? (
            <Uyari tur="hata" baslik="Şablon indirilemedi">
              {hataMetni(indirmeHatasi)}
            </Uyari>
          ) : null}
        </div>
      </Kart>

      <Kart baslik="2 · Dosyayı yükleyin">
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (dosya) yukle.mutate(dosya);
          }}
        >
          <div className="flex flex-col gap-1">
            <label
              htmlFor="ice-aktarim-dosya"
              className="text-sm font-semibold text-murekkep-2"
            >
              Liste dosyası
            </label>
            <input
              id="ice-aktarim-dosya"
              type="file"
              accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              aria-describedby="ice-aktarim-dosya-ipucu"
              onChange={(e) => {
                const secilen = e.target.files?.[0] ?? null;
                setDosya(secilen);
              }}
              className="min-h-hedef rounded-lg border border-cetvel bg-yuzey px-3 py-2 text-md text-murekkep"
            />
            <p
              id="ice-aktarim-dosya-ipucu"
              className="m-0 text-xs text-murekkep-3"
            >
              En fazla 5.000 satır. Bu adım kayıt OLUŞTURMAZ.
            </p>
          </div>

          <IslemDurumu hata={yukle.error} />

          <div className="flex flex-wrap items-center gap-2">
            <Buton
              type="submit"
              tur="birincil"
              disabled={!dosya}
              yukleniyor={yukle.isPending}
            >
              Önizleme üret
            </Buton>
            {dosya && (
              <span className="text-sm text-murekkep-3">{dosya.name}</span>
            )}
          </div>
        </form>
      </Kart>

      {aktarimId && (
        <Onizleme
          aktarimId={aktarimId}
          sifirla={() => {
            setAktarimId(null);
            setDosya(null);
            yukle.reset();
            void istemci.invalidateQueries({ queryKey: ["kisi"] });
          }}
        />
      )}
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------
// Önizleme
// ---------------------------------------------------------------------------

function Onizleme({
  aktarimId,
  sifirla,
}: {
  aktarimId: string;
  sifirla: () => void;
}) {
  const istemci = useQueryClient();
  const [sayfa, setSayfa] = useState(1);
  const [suzgec, setSuzgec] = useState("");
  const [gozdenGecirildi, setGozdenGecirildi] = useState(false);
  const [secilenCakismalar, setSecilenCakismalar] = useState<string[]>([]);

  /** Üst bant: her zaman özet + gözden geçirilmesi gereken satırlar. */
  const ozetSorgu = useQuery({
    queryKey: ["ice-aktarim", aktarimId, "ozet"],
    queryFn: () =>
      apiOku<OnizlemeYaniti>(`/ice-aktarim/${aktarimId}`, {
        sayfa: 1,
        boyut: 1,
      }),
  });

  const hataSorgu = useQuery({
    queryKey: ["ice-aktarim", aktarimId, "hata"],
    queryFn: () =>
      apiOku<OnizlemeYaniti>(`/ice-aktarim/${aktarimId}`, {
        sayfa: 1,
        boyut: 200,
        sonuc: SatirSonucu.HATA,
      }),
  });

  const cakismaSorgu = useQuery({
    queryKey: ["ice-aktarim", aktarimId, "cakisma"],
    queryFn: () =>
      apiOku<OnizlemeYaniti>(`/ice-aktarim/${aktarimId}`, {
        sayfa: 1,
        boyut: 200,
        sonuc: SatirSonucu.CAKISMA,
      }),
  });

  const listeSorgu = useQuery({
    queryKey: ["ice-aktarim", aktarimId, "liste", suzgec, sayfa],
    queryFn: () =>
      apiOku<OnizlemeYaniti>(`/ice-aktarim/${aktarimId}`, {
        sayfa,
        boyut: SAYFA_BOYU,
        sonuc: suzgec || undefined,
      }),
  });

  const uygula = useMutation({
    mutationFn: async (): Promise<UygulamaSonucu> => {
      if (secilenCakismalar.length === 0) {
        // Gövde boş: sunucu yalnızca YENI ve GUNCELLEME kararlarını uygular,
        // çakışmalar ATLANDI olur.
        return apiYaz<UygulamaSonucu>(
          "POST",
          `/ice-aktarim/${aktarimId}/uygula`,
          {},
        );
      }
      // Çakışma seçildiyse sunucu YALNIZCA verilen id'leri uygular; bu yüzden
      // net kararların id'leri de listeye eklenmelidir.
      const [yeniler, guncellemeler] = await Promise.all([
        tumSatirIdleri(aktarimId, SatirSonucu.YENI),
        tumSatirIdleri(aktarimId, SatirSonucu.GUNCELLEME),
      ]);
      return apiYaz<UygulamaSonucu>(
        "POST",
        `/ice-aktarim/${aktarimId}/uygula`,
        { satir_idler: [...yeniler, ...guncellemeler, ...secilenCakismalar] },
      );
    },
    onSuccess: () => {
      void istemci.invalidateQueries({ queryKey: ["ice-aktarim", aktarimId] });
      void istemci.invalidateQueries({ queryKey: ["kisi"] });
    },
  });

  const iptal = useMutation({
    mutationFn: () =>
      apiYaz<AktarimOzeti>("POST", `/ice-aktarim/${aktarimId}/iptal`),
    onSuccess: () => {
      void istemci.invalidateQueries({ queryKey: ["ice-aktarim", aktarimId] });
    },
  });

  const ozet = ozetSorgu.data;
  const hatalar = hataSorgu.data?.satirlar ?? [];
  const cakismalar = cakismaSorgu.data?.satirlar ?? [];
  const incelenecek = hatalar.length + cakismalar.length;
  const sonuclandi =
    ozet !== undefined && ozet.durum !== AktarimDurumu.ONIZLEME_HAZIR;

  // "Uygula" düğmesi, incelenecek satır varsa AÇIK ONAY olmadan çalışmaz.
  const uygulanabilir =
    !sonuclandi && (incelenecek === 0 || gozdenGecirildi) && !uygula.isPending;

  return (
    <>
      <Kart
        baslik="3 · Önizleme"
        ustBilgi={
          ozet && (
            <span className="text-sm text-murekkep-3">
              {ozet.dosya_adi} · {tarihSaatGoster(ozet.olusma_zamani)}
            </span>
          )
        }
      >
        <SorguDurumu
          yukleniyor={ozetSorgu.isPending}
          hata={ozetSorgu.error}
          bos={!ozet}
          bosBaslik="Önizleme bulunamadı"
          yenile={() => void ozetSorgu.refetch()}
        >
          {ozet && (
            <div className="flex flex-col gap-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <IstatistikKarosu
                  etiket="Toplam satır"
                  deger={
                    <span className="rakam font-mono">{ozet.toplam_satir}</span>
                  }
                />
                <IstatistikKarosu
                  etiket="Yeni kayıt"
                  deger={
                    <span className="rakam font-mono">{ozet.yeni_sayisi}</span>
                  }
                />
                <IstatistikKarosu
                  etiket="Güncelleme"
                  deger={
                    <span className="rakam font-mono">
                      {ozet.guncellenen_sayisi}
                    </span>
                  }
                />
                <IstatistikKarosu
                  etiket="Çakışma"
                  deger={
                    <span className="rakam font-mono">
                      {ozet.cakisan_sayisi}
                    </span>
                  }
                  yardimci="Karar gerekli"
                  vurgu={ozet.cakisan_sayisi > 0}
                />
                <IstatistikKarosu
                  etiket="Hatalı satır"
                  deger={
                    <span className="rakam font-mono">
                      {ozet.hatali_sayisi}
                    </span>
                  }
                  yardimci="Uygulanmaz"
                  vurgu={ozet.hatali_sayisi > 0}
                />
              </div>

              {sonuclandi && (
                <Uyari
                  tur={ozet.durum === AktarimDurumu.UYGULANDI ? "onay" : "notr"}
                  baslik={
                    ozet.durum === AktarimDurumu.UYGULANDI
                      ? "Bu içe aktarım uygulandı"
                      : "Bu içe aktarım iptal edildi"
                  }
                >
                  {ozet.uygulama_zamani
                    ? tarihSaatGoster(ozet.uygulama_zamani)
                    : "Yeni bir dosya yükleyerek devam edebilirsiniz."}
                </Uyari>
              )}
            </div>
          )}
        </SorguDurumu>
      </Kart>

      {/* Çakışma ve hatalar EN ÜSTE — aşağıda kaybolmaları, gözden kaçmaları
          demektir ve gözden kaçan bir çakışma mükerrer öğrenci üretir. */}
      <Kart
        baslik="Gözden geçirilmesi gerekenler"
        ustBilgi={
          <span className="text-sm text-murekkep-3">
            <span className="rakam font-mono">{incelenecek}</span> satır
          </span>
        }
      >
        <SorguDurumu
          yukleniyor={hataSorgu.isPending || cakismaSorgu.isPending}
          hata={hataSorgu.error ?? cakismaSorgu.error}
          bos={incelenecek === 0}
          bosBaslik="İncelenecek satır yok"
          bosAciklama="Dosyadaki her satır net bir karara bağlandı. Aşağıdan uygulayabilirsiniz."
          yenile={() => {
            void hataSorgu.refetch();
            void cakismaSorgu.refetch();
          }}
        >
          <div className="flex flex-col gap-4">
            {hatalar.length > 0 && (
              <section aria-label="Hatalı satırlar">
                <h3 className="m-0 mb-2 text-lg">
                  Hatalı satırlar{" "}
                  <span className="rakam font-mono text-murekkep-3">
                    ({hatalar.length})
                  </span>
                </h3>
                <p className="m-0 mb-2 text-sm text-murekkep-2">
                  Bu satırlar <strong>hiçbir koşulda uygulanmaz</strong>.
                  Dosyayı düzeltip yeniden yükleyin.
                </p>
                <SatirTablosu satirlar={hatalar} />
              </section>
            )}

            {cakismalar.length > 0 && (
              <section aria-label="Çakışan satırlar">
                <h3 className="m-0 mb-2 text-lg">
                  Çakışmalar{" "}
                  <span className="rakam font-mono text-murekkep-3">
                    ({cakismalar.length})
                  </span>
                </h3>
                <p className="m-0 mb-2 text-sm text-murekkep-2">
                  Çakışma <strong>insan kararı bekler</strong>. İşaretlemediğin
                  satırlar atlanır; işaretlediklerin mevcut kaydın üzerine
                  yazılır.
                </p>
                <SatirTablosu
                  satirlar={cakismalar}
                  secim={{
                    secilenler: secilenCakismalar,
                    degistir: (id, isaretli) =>
                      setSecilenCakismalar((onceki) =>
                        isaretli
                          ? [...onceki, id]
                          : onceki.filter((x) => x !== id),
                      ),
                  }}
                />
              </section>
            )}
          </div>
        </SorguDurumu>
      </Kart>

      <Kart
        baslik="Tüm satırlar"
        ustBilgi={
          <div className="w-56">
            <Secim
              etiket="Sonuca göre süz"
              value={suzgec}
              onChange={(e) => {
                setSuzgec(e.target.value);
                setSayfa(1);
              }}
              secenekler={[
                { deger: "", metin: "Hepsi" },
                { deger: String(SatirSonucu.HATA), metin: "Hatalı" },
                { deger: String(SatirSonucu.CAKISMA), metin: "Çakışma" },
                { deger: String(SatirSonucu.GUNCELLEME), metin: "Güncelleme" },
                { deger: String(SatirSonucu.YENI), metin: "Yeni" },
                { deger: String(SatirSonucu.ATLANDI), metin: "Atlandı" },
              ]}
            />
          </div>
        }
      >
        <SorguDurumu
          yukleniyor={listeSorgu.isPending}
          hata={listeSorgu.error}
          bos={(listeSorgu.data?.satirlar.length ?? 0) === 0}
          bosBaslik="Bu süzgeçte satır yok"
          yenile={() => void listeSorgu.refetch()}
        >
          <SatirTablosu satirlar={listeSorgu.data?.satirlar ?? []} />
          <Sayfalayici
            sayfa={listeSorgu.data?.sayfa ?? sayfa}
            boyut={listeSorgu.data?.boyut ?? SAYFA_BOYU}
            toplam={listeSorgu.data?.satir_toplami ?? 0}
            degistir={setSayfa}
          />
        </SorguDurumu>
      </Kart>

      <Kart baslik="4 · Uygula">
        <div className="flex flex-col gap-3">
          {incelenecek > 0 && !sonuclandi && (
            <OnayKutusu
              etiket="Çakışma ve hatalı satırları gözden geçirdim"
              aciklama="Bu kutu işaretlenmeden uygulama başlatılamaz. Hatalı satırlar hiçbir koşulda uygulanmaz."
              isaretli={gozdenGecirildi}
              degistir={setGozdenGecirildi}
            />
          )}

          <p className="m-0 text-sm text-murekkep-2">
            Uygulanacak:{" "}
            <span className="rakam font-mono">{ozet?.yeni_sayisi ?? 0}</span>{" "}
            yeni,{" "}
            <span className="rakam font-mono">
              {ozet?.guncellenen_sayisi ?? 0}
            </span>{" "}
            güncelleme
            {secilenCakismalar.length > 0 && (
              <>
                ,{" "}
                <span className="rakam font-mono">
                  {secilenCakismalar.length}
                </span>{" "}
                seçilmiş çakışma
              </>
            )}
            . Tümü <strong>tek işlemde</strong> yazılır; yarım kalmaz.
          </p>

          <IslemDurumu hata={uygula.error ?? iptal.error} />

          {uygula.data && (
            <Uyari tur="onay" baslik="İçe aktarım uygulandı">
              <p className="m-0">
                Oluşturulan:{" "}
                <span className="rakam font-mono">
                  {uygula.data.olusturulan}
                </span>{" "}
                · Güncellenen:{" "}
                <span className="rakam font-mono">
                  {uygula.data.guncellenen}
                </span>{" "}
                · Atlanan:{" "}
                <span className="rakam font-mono">{uygula.data.atlanan}</span>
              </p>
              {uygula.data.hatalar.length > 0 && (
                <ul className="m-0 mt-2 list-disc pl-5">
                  {uygula.data.hatalar.map((h) => (
                    <li key={h.satir_no}>
                      Satır {h.satir_no}: {h.mesaj}
                    </li>
                  ))}
                </ul>
              )}
            </Uyari>
          )}

          <div className="flex flex-wrap gap-2">
            <Buton
              tur="birincil"
              disabled={!uygulanabilir}
              yukleniyor={uygula.isPending}
              onClick={() => uygula.mutate()}
            >
              Uygula
            </Buton>
            <Buton
              tur="ikincil"
              disabled={sonuclandi || iptal.isPending}
              yukleniyor={iptal.isPending}
              onClick={() => iptal.mutate()}
            >
              Bu aktarımı iptal et
            </Buton>
            <Buton tur="sessiz" onClick={sifirla}>
              Yeni dosya yükle
            </Buton>
          </div>
        </div>
      </Kart>
    </>
  );
}

/** Belirli bir sonuç tipindeki TÜM satır id'lerini sayfalayarak toplar. */
async function tumSatirIdleri(
  aktarimId: string,
  sonuc: number,
): Promise<string[]> {
  const idler: string[] = [];
  let sayfa = 1;
  for (;;) {
    const yanit = await apiOku<OnizlemeYaniti>(`/ice-aktarim/${aktarimId}`, {
      sayfa,
      boyut: TOPLU_BOYUT,
      sonuc,
    });
    for (const s of yanit.satirlar) idler.push(s.id);
    if (yanit.satirlar.length < TOPLU_BOYUT) return idler;
    sayfa += 1;
    // Sunucu tarafı tavanı 5.000 satır; sonsuz döngüye karşı sert sınır.
    if (sayfa > 20) return idler;
  }
}

// ---------------------------------------------------------------------------

const SONUC_ROZETI: Record<
  number,
  { tur: "onay" | "bilgi" | "uyari" | "hata" | "notr"; metin: string }
> = {
  [SatirSonucu.YENI]: { tur: "onay", metin: "yeni" },
  [SatirSonucu.GUNCELLEME]: { tur: "bilgi", metin: "güncelleme" },
  [SatirSonucu.CAKISMA]: { tur: "uyari", metin: "çakışma" },
  [SatirSonucu.HATA]: { tur: "hata", metin: "hata" },
  [SatirSonucu.ATLANDI]: { tur: "notr", metin: "atlandı" },
};

function SatirTablosu({
  satirlar,
  secim,
}: {
  satirlar: OnizlemeSatiri[];
  secim?: {
    secilenler: string[];
    degistir: (id: string, isaretli: boolean) => void;
  };
}) {
  return (
    <Tablo>
      <TabloBaslik>
        {secim && (
          <th scope="col">
            <span className="gizli-erisilebilir">Uygula</span>
          </th>
        )}
        <th scope="col">Satır</th>
        <th scope="col">Sonuç</th>
        <th scope="col">Kişi</th>
        <th scope="col">Sınıf / No</th>
        <th scope="col">Mesaj</th>
      </TabloBaslik>
      <TabloGovde>
        {satirlar.map((s) => {
          const rozet = SONUC_ROZETI[s.sonuc] ?? {
            tur: "notr" as const,
            metin: s.sonuc_adi,
          };
          const ad = s.alanlar
            ? `${s.alanlar.ad} ${s.alanlar.soyad}`
            : (s.kaynak["Ad"] ?? "—");
          return (
            <tr key={s.id}>
              {secim && (
                <td>
                  <input
                    type="checkbox"
                    checked={secim.secilenler.includes(s.id)}
                    onChange={(e) => secim.degistir(s.id, e.target.checked)}
                    aria-label={`Satır ${s.satir_no} — ${ad} için çakışmayı uygula`}
                    className="h-4 w-4 accent-simit"
                  />
                </td>
              )}
              <td className="rakam font-mono text-xs">{s.satir_no}</td>
              <td>
                <Rozet tur={rozet.tur}>{rozet.metin}</Rozet>
              </td>
              <td>
                <p className="m-0 font-semibold text-murekkep">{ad}</p>
                {s.eslesen_kisi && (
                  <p className="m-0 text-xs text-murekkep-3">
                    Mevcut kayıt: {s.eslesen_kisi}
                  </p>
                )}
              </td>
              <td className="text-xs text-murekkep-2">
                {s.alanlar?.sinif ?? s.kaynak["Sinif"] ?? "—"}
                {(s.alanlar?.numara ?? s.kaynak["Numara"]) && (
                  <span className="rakam block font-mono">
                    No {s.alanlar?.numara ?? s.kaynak["Numara"]}
                  </span>
                )}
              </td>
              <td className="text-sm text-murekkep-2">{s.mesaj ?? "—"}</td>
            </tr>
          );
        })}
      </TabloGovde>
    </Tablo>
  );
}
